import type { ProductStep } from "../../products";

export interface OnboardingProgressItem {
  id: string;
  label: string;
}

export interface OnboardingProgress {
  activeItemIndex: number;
  isComplete: boolean;
  items: OnboardingProgressItem[];
  visibleStepCount: number;
  visibleStepIndex: number;
}

export function deriveOnboardingProgress(
  steps: ProductStep[],
  currentPath: string | undefined,
): OnboardingProgress {
  const visibleSteps = steps.filter(
    (step) => !step.hideFromNav && !step.shouldSkip?.(),
  );
  const useGroups = steps.some((step) => step.navGroup);
  const items: OnboardingProgressItem[] = [];
  const stepToItemIndex = new Map<string, number>();

  if (useGroups) {
    const seenGroups = new Set<string>();
    for (const step of visibleSteps) {
      const group = step.navGroup;
      if (!group) {
        continue;
      }
      if (!seenGroups.has(group)) {
        seenGroups.add(group);
        items.push({ id: `group-${group}`, label: group });
      }
      stepToItemIndex.set(step.path, items.length - 1);
    }
  } else {
    for (const step of visibleSteps) {
      stepToItemIndex.set(step.path, items.length);
      items.push({ id: step.id, label: step.label });
    }
  }

  const allStepsIndex = steps.findIndex((step) => step.path === currentPath);
  const lastVisibleStepIndex = steps.reduce(
    (lastIndex, step, index) =>
      visibleSteps.includes(step) ? index : lastIndex,
    -1,
  );
  const activeItemIndex = currentPath
    ? (stepToItemIndex.get(currentPath) ?? -1)
    : -1;

  return {
    activeItemIndex,
    isComplete: activeItemIndex === -1 && allStepsIndex > lastVisibleStepIndex,
    items,
    visibleStepCount: visibleSteps.length,
    visibleStepIndex: visibleSteps.findIndex(
      (step) => step.path === currentPath,
    ),
  };
}
